/**
 * Workflows P6 (DEC-099): the steps that make a workflow the source of truth for staff alerts, assignment and
 * developer-webhook delivery. Mirrors backend `app/core/constants/workflow_p6.py`.
 *
 * The four steps are not in the add-step menu yet: they belong to the default workflows, and the builder gains their
 * forms with the Default workflows section. `convertNotifyStaff` is the "Older step: convert" mapping.
 */
import type { WorkflowTrigger } from "./workflows";

/** The steps P6 adds. `route_handoff` and `assign_lead_owner` act at the call site of their event, so only a default holds them. */
export const WORKFLOW_P6_STEP_TYPES = ["notify_team", "route_handoff", "assign_lead_owner", "send_to_subscribed_endpoints"] as const;
export type WorkflowP6StepType = (typeof WORKFLOW_P6_STEP_TYPES)[number];

/** Steps only an inline (default) workflow may hold. */
export const INLINE_ONLY_STEPS: ReadonlySet<string> = new Set(["route_handoff", "assign_lead_owner"]);
/** The one trigger a P6 step works on. */
export const STEP_ONLY_TRIGGER: Readonly<Record<string, WorkflowTrigger>> = {
  route_handoff: "handoff_requested",
  assign_lead_owner: "lead_created",
  send_to_subscribed_endpoints: "platform_event",
};

export const NOTIFY_TARGETS_MAX = 8;
/** Who a `notify_team` target resolves to. */
export const NOTIFY_TEAM_TARGETS = [
  "assignee",
  "handoff_audience",
  "queue_audience",
  "reminder_audience",
  "returned_audience",
  "team",
  "team_of_conversation",
  "team_lead",
  "team_mailbox",
  "lead_owner",
  "leads_mailbox",
  "gap_assignee",
  "source_uploader",
  "owners",
  "owners_admins",
  "owners_admins_for_agent",
  "workspace_owner",
  "permission",
  "user",
  // Service Desk: the people of a ticket (`assignee`, `team_lead`, `team_mailbox`, `team` and `permission` read the ticket when the run has one).
  "ticket_audience",
  "team_of_ticket",
  "mentioned",
] as const;
export type NotifyTarget = (typeof NOTIFY_TEAM_TARGETS)[number];
/** Targets that name an address, not a member. */
export const MAILBOX_TARGETS: ReadonlySet<NotifyTarget> = new Set(["team_mailbox", "leads_mailbox"]);
/** The targets whose notice is handed to a member personally today: only these may be notified `direct`. */
export const DIRECT_TARGETS: ReadonlySet<NotifyTarget> = new Set(["assignee", "queue_audience", "handoff_audience", "ticket_audience", "mentioned"]);

export const NOTIFY_DELIVERIES = ["preferences", "direct"] as const;
export type NotifyDelivery = (typeof NOTIFY_DELIVERIES)[number];
export const OUT_OF_HOURS_RULES = ["same", "in_app_only"] as const;
export type OutOfHours = (typeof OUT_OF_HOURS_RULES)[number];
export const CHANNELS_FROM = ["source_notice"] as const;
export const MESSAGE_TEMPLATES = ["system", "custom"] as const;
export type MessageTemplate = (typeof MESSAGE_TEMPLATES)[number];
/** The notification types a workflow may send (not billing, security or platform kinds). */
export const NOTIFIABLE_CATEGORIES = [
  "handoff_requested",
  "source_processed",
  "source_failed",
  "knowledge_gap",
  "lead_assigned",
  "new_lead",
  "new_booking",
  "workflow",
  "ticket_new",
  "ticket_assigned",
  "ticket_client_replied",
  "ticket_mention",
  "ticket_sla",
  "ticket_csat_low",
  "ticket_returned_to_queue",
  "order_placed",
  "order_issue",
  "order_sync_failed",
  "order_approval",
  // After-sales (M27 S3): a visit is booked or moved, an asset or a contract falls due
  "visit_scheduled",
  "visit_changed",
  "asset_due",
] as const;
export type NotifiableCategory = (typeof NOTIFIABLE_CATEGORIES)[number];
export const DEFAULT_CATEGORY: NotifiableCategory = "workflow";
export const NOTIFY_CUSTOM_LIMITS = { title: 120, body: 500 } as const;

/** Triggers whose events carry the member who acted (`exclude_actor` needs one). */
export const ACTOR_TRIGGERS: ReadonlySet<WorkflowTrigger> = new Set([
  "conversation_assigned",
  "lead_updated",
  "gap_assigned",
  "ticket_created",
  "ticket_updated",
  "ticket_state_changed",
  "ticket_assigned",
  "ticket_customer_replied",
  "ticket_staff_replied",
  "ticket_note_added",
  "ticket_resolved",
  "ticket_reopened",
]);
/** Triggers that carry the handoff's own office clock (`out_of_hours` needs one). */
export const OFFICE_OPEN_TRIGGERS: ReadonlySet<WorkflowTrigger> = new Set(["handoff_requested", "handoff_reminder_due", "handoff_escalation_due", "handoff_waiting_digest_due"]);
/** Triggers about a finished knowledge sync (`channels_from` and `source.notice`). */
export const SOURCE_NOTICE_TRIGGERS: ReadonlySet<WorkflowTrigger> = new Set(["source_processed", "source_failed"]);
/** Triggers that have a standard message: the wording today's code sends for them. */
export const SYSTEM_MESSAGE_TRIGGERS: ReadonlySet<WorkflowTrigger> = new Set([
  "handoff_requested",
  "handoff_reminder_due",
  "handoff_escalation_due",
  "handoff_waiting_digest_due",
  "conversation_assigned",
  "conversation_returned_to_queue",
  "lead_created",
  "lead_followed_up",
  "lead_updated",
  "booking_requested",
  "booking_confirmed",
  "source_processed",
  "source_failed",
  "gap_assigned",
  "ticket_created",
  "ticket_updated",
  "ticket_state_changed",
  "ticket_assigned",
  "ticket_returned_to_queue",
  "ticket_customer_replied",
  "ticket_staff_replied",
  "ticket_note_added",
  "ticket_sla_warning",
  "ticket_sla_breached",
  "ticket_unassigned_due",
  "ticket_waiting_reminder_due",
  "ticket_pending_expired",
  "ticket_resolved",
  "ticket_reopened",
  "ticket_auto_close_due",
  "ticket_csat_received",
  // Orders (M27 S2): an order placed, needing an approval, or that could not be sent
  "order_placed",
  "order_approval_requested",
  "order_sync_failed",
  // After-sales (M27 S3): a visit scheduled, moved on or reminded, an RMA moved
  "visit_scheduled",
  "visit_status_changed",
  "visit_reminder_due",
  "rma_status_changed",
]);

export const ROUTE_MODES = ["agent_settings", "team", "user"] as const;
export type RouteMode = (typeof ROUTE_MODES)[number];
export const ROUND_ROBIN_SETTINGS = ["team_setting", "on", "off"] as const;
export type RoundRobinSetting = (typeof ROUND_ROBIN_SETTINGS)[number];
export const LEAD_OWNER_MODES = ["workspace_setting", "round_robin", "user"] as const;
export type LeadOwnerMode = (typeof LEAD_OWNER_MODES)[number];

/** How a finished knowledge sync is reported (`source.notice`) and what started it (`source.run_kind`). */
export const SOURCE_NOTICES = ["none", "in_app", "email", "in_app_email"] as const;
export const SOURCE_RUN_KINDS = ["first", "manual", "scheduled"] as const;

/** A `notify_staff` step as the definition stores it. */
export interface NotifyStaffStep {
  id: string;
  type: "notify_staff";
  name?: string;
  to: "user" | "team" | "owners_admins" | "assignee" | "lead_owner";
  user_id?: string | null;
  team_id?: string | null;
  title: string;
  body?: string | null;
  email?: boolean;
}

export interface NotifyTeamStep {
  id: string;
  type: "notify_team";
  name?: string;
  category: NotifiableCategory;
  to: { target: NotifyTarget; delivery?: NotifyDelivery; team_id?: string; user_id?: string }[];
  channels?: ("in_app" | "email")[];
  message: { template: "custom"; title: string; body?: string };
}

const CONVERTED_TARGET: Readonly<Record<NotifyStaffStep["to"], NotifyTarget>> = {
  user: "user",
  team: "team",
  owners_admins: "owners_admins_for_agent",
  assignee: "assignee",
  lead_owner: "lead_owner",
};

/**
 * The `notify_team` step an older `notify_staff` step becomes. The message and the people stay; the old step's
 * `email` switch becomes what it meant: `email: true` bypassed the member's settings, which a `notify_team` step
 * allows only for the assignee (`delivery: direct`), and `email: false` was a bell item only (`channels: ["in_app"]`).
 */
export function convertNotifyStaff(step: NotifyStaffStep): NotifyTeamStep {
  const target = CONVERTED_TARGET[step.to];
  const email = step.email ?? true;
  const recipient: NotifyTeamStep["to"][number] = { target };
  if (step.to === "team" && step.team_id) recipient.team_id = step.team_id;
  if (step.to === "user" && step.user_id) recipient.user_id = step.user_id;
  if (email && DIRECT_TARGETS.has(target)) recipient.delivery = "direct";
  const converted: NotifyTeamStep = {
    id: step.id,
    type: "notify_team",
    category: "workflow",
    to: [recipient],
    message: { template: "custom", title: step.title, ...(step.body ? { body: step.body } : {}) },
  };
  if (step.name) converted.name = step.name;
  if (!email) converted.channels = ["in_app"];
  return converted;
}
